/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { map as _map, uniq as _uniq } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import {
  Link as RouterLink,
  matchPath,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
} from 'react-router';

import AccountTreeIcon from '@mui/icons-material/AccountTreeRounded';
import GroupsIcon from '@mui/icons-material/GroupsRounded';
import InfoIcon from '@mui/icons-material/InfoRounded';
import KeyIcon from '@mui/icons-material/KeyRounded';
import PersonIcon from '@mui/icons-material/PersonRounded';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import WidgetsIcon from '@mui/icons-material/WidgetsRounded';
import { Breadcrumbs, Button, Grid, Tab, Tabs, Typography } from '@mui/material';

import { OrganizationManageClustersList } from '../../components/ClustersList/OrganizationManage/OrganizationClustersList';
import { HUSpin } from '../../components/HUSpin/HUSpin';
import { InvitedToOrganizationList } from '../../components/InvitedToOrganizationList/InvitedToOrganizationList';
import { OrganizationMembersList } from '../../components/MembersList/Organization/OrganizationMembersList';
import { OrganizationManageProjectsList } from '../../components/ProjectsList/OrganizationManage/OrganizationProjectsList';
import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../components/SimpleNotifyModal/SimpleNotifyModal';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { ProjectApiService } from '../../services/ProjectApiService';
import { RootState } from '../../store';
import { openCreateProjectOverlay } from '../../store/createProject/actions';
import { OpenCreateProjectOverlayParams } from '../../store/createProject/types';
import { ApiClientError } from '../../types/api/ApiClientError';
import { EntityId } from '../../types/api/EntityId';
import { IProjectWithClusters } from '../../types/project/IProjectWithClusters';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import { fetchOrganization, fetchProjectsWithClusters } from '../../utils/queryFns';
import { reactToDeleteProject } from '../../utils/reactions';
import { useControlOverlays } from '../../utils/useControlOverlays';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useIsMounted } from '../../utils/useIsMounted';
import { useObjectState } from '../../utils/useObjectState';
import { useOrganizationPermissions } from '../../utils/useOrganizationPermissions';
import { DetailsTab } from './components/DetailsTab/DetailsTab';
import { InviteMember } from './components/InviteMember/InviteMember';
import { SsoTab } from './components/SsoTab/SsoTab';
import { TeamsTab } from './components/TeamsTab/TeamsTab';
import { ManageOrganizationTabs, tabToRouteBinding } from './ManageOrganizationPage.consts';
import { createManageOrganizationPageStyles } from './ManageOrganizationPage.styles';

const mapStateToProps = (state: RootState) => ({
  organizationDetails: state.organizationDetails,
});
const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PathParamsType = {
  organization_id: string;
};

type IProps = PropsFromRedux;

type State = {
  selectedProjects: EntityId[];
  deleteProjectsErrors: { [projectName: string]: string };
  isDeletingProjects: boolean;
};

const ManageOrganizationPageComponent = memo<IProps>(() => {
  const { t } = useTranslation([Namespaces.OrganizationAdmin, Namespaces.Navigation]);
  const classes = createManageOrganizationPageStyles();

  const location = useLocation();
  const baseUrl = useResolvedPath('').pathname;
  const params = useParams<PathParamsType>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const organizationId = params.organization_id!;
  const permissions = useOrganizationPermissions(organizationId);

  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);

  const { doOpenCreateServiceOverlay } = useControlOverlays();

  const handleProjectClick = (project: IProjectWithClusters, manage = false) => {
    void navigate(
      prepareRoutePath(manage ? BasicRoutes.MANAGE_PROJECT : BasicRoutes.PROJECT_PAGE, {
        ':organization_id': organizationId,
        ':project_id': project.id,
      }),
    );
  };

  const handleManageClusterClick = (
    project: IProjectWithClusters,
    cluster: IProjectWithClusters['clusters'][number],
  ) => {
    void navigate(
      prepareRoutePath(BasicRoutes.CLUSTER_PAGE, {
        ':organization_id': organizationId,
        ':project_id': project.id,
        ':cluster_id': cluster.id,
      }),
    );
  };

  const organizationQuery = useQuery([fetchOrganization.key, { organizationId }], fetchOrganization.fn);
  const projectsWithClustersQuery = useQuery(
    [fetchProjectsWithClusters.key, { organizationId }],
    fetchProjectsWithClusters.fn,
  );

  const organization = useMemo(() => organizationQuery.data?.data.payload, [organizationQuery.data]);
  useDocumentTitle(
    t(`${Namespaces.Common}:page_titles.organization_manage`, {
      organization: organization?.name,
    }),
  );

  const projectsWithClusters = useMemo(
    () => projectsWithClustersQuery.data?.data.payload || [],
    [projectsWithClustersQuery.data],
  );

  const isFetching = useMemo(
    () => [organizationQuery.status, projectsWithClustersQuery.status].some(v => v === 'loading'),
    [organizationQuery.status, projectsWithClustersQuery.status],
  );

  const getPathForTab = (tab: ManageOrganizationTabs) => {
    return `${baseUrl}/${tabToRouteBinding[tab].tab}`;
  };

  const getPathForRoute = (tab: ManageOrganizationTabs) => {
    return `${baseUrl}/${tabToRouteBinding[tab].route}`;
  };

  const onCreateProjectClick = () => {
    const params: OpenCreateProjectOverlayParams = {
      organizationId,
    };
    dispatch(openCreateProjectOverlay(params));
  };

  const [state, setState] = useObjectState<State>({
    selectedProjects: [],
    deleteProjectsErrors: {},
    isDeletingProjects: false,
  });

  const { deleteProjectsErrors, selectedProjects, isDeletingProjects } = state;

  const toggleSelectedProject = useCallback(
    (projectId: EntityId, checked: boolean) => {
      setState(prevState => {
        return {
          ...prevState,
          selectedProjects: checked
            ? [..._uniq(prevState.selectedProjects.concat([projectId]))]
            : prevState.selectedProjects.filter(i => i !== projectId),
        };
      });
    },
    [setState],
  );

  const isMounted = useIsMounted();

  useEffect(() => {
    setState(prevState => {
      const nextSelectedProjects: EntityId[] = [];
      prevState.selectedProjects.forEach(i => {
        const projectExists = projectsWithClusters.findIndex(project => project.id === i) > -1;
        if (projectExists) {
          nextSelectedProjects.push(i);
        }
      });
      return {
        ...prevState,
        selectedProjects: nextSelectedProjects,
      };
    });
  }, [projectsWithClusters, setState]);

  const selectedProjectsNames: string[] = useMemo(() => {
    const result: string[] = [];
    selectedProjects.forEach(id => {
      const project = projectsWithClusters.find(project => project.id === id);
      project && result.push(project.name);
    });
    return result;
  }, [projectsWithClusters, selectedProjects]);

  const onConfirmDeleteProject = async () => {
    setIsOpenModal(false);
    setState({ deleteProjectsErrors: {}, isDeletingProjects: true });
    let shouldOpenErrorModal = false;
    const promises = selectedProjects.map(async projectId => {
      const project = projectsWithClusters.find(project => project.id === projectId);
      try {
        if (!project) {
          return;
        }
        await ProjectApiService.deleteProject({
          organizationId,
          projectId: project.id,
        });
        if (!isMounted.current) {
          return;
        }
      } catch (e) {
        const error = e as ApiClientError;
        if (error.response) {
          shouldOpenErrorModal = true;
          setState(prevState => {
            return {
              ...prevState,
              deleteProjectsErrors: {
                ...prevState.deleteProjectsErrors,
                [project!.name]: error.response?.data.message,
              },
            };
          });
        }
      }
    });
    await Promise.all(promises);
    if (!isMounted.current) {
      return;
    }
    setState({ selectedProjects: [], isDeletingProjects: false });
    shouldOpenErrorModal && setIsOpenErrorModal(true);
    selectedProjects.length && void reactToDeleteProject({ organizationId });
  };

  const renderTabContent = () => (
    <Routes>
      <Route index element={organization && <DetailsTab organization={organization} />} />
      <Route
        path={tabToRouteBinding[ManageOrganizationTabs.Projects].route}
        element={
          <HUSpin spinning={isDeletingProjects}>
            <OrganizationManageProjectsList
              projects={projectsWithClusters}
              onClick={p => handleProjectClick(p)}
              onManageClick={p => handleProjectClick(p, true)}
              toggleSelectedProject={toggleSelectedProject}
              selectedProjects={selectedProjects}
            />
            {permissions.create_project && (
              <Button
                onClick={onCreateProjectClick}
                className={classes.createButton}
                data-cy="projects-tab-add-project-button"
              >
                {t('management.projects_list.create')}
              </Button>
            )}
            {!!selectedProjects.length && permissions.edit_organization && (
              <div className={classes.bottomDeleteRow}>
                <RemoveCircleIcon classes={{ root: classes.bottomDeleteIcon }} color="primary" />
                <span className={classes.bottomDeleteLabel}>
                  {t('project_selected', {
                    count: selectedProjects.length,
                  })}
                </span>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => setIsOpenModal(true)}
                  data-cy="projects-tab-project-delete-button"
                >
                  {t(`${Namespaces.Common}:form.delete`)}
                </Button>
              </div>
            )}
            <SimpleConfirmModal
              open={isOpenModal}
              message={t(`${Namespaces.Common}:confirmation.delete_project`, {
                name: selectedProjectsNames,
                count: selectedProjects.length,
              })}
              onCancel={() => setIsOpenModal(false)}
              onConfirm={onConfirmDeleteProject}
            />
            <SimpleNotifyModal
              open={isOpenErrorModal}
              message={_map(deleteProjectsErrors, (error, projectName) => (
                <React.Fragment key={[error, projectName].join('_')}>
                  {t(
                    [
                      `${Namespaces.Common}:delete_entity_error.${error}`,
                      `${Namespaces.Common}:errors.unexpected_error`,
                    ],
                    { entity_name: projectName },
                  )}
                  <br />
                </React.Fragment>
              ))}
              onOk={() => {
                setIsOpenErrorModal(false);
              }}
            />
          </HUSpin>
        }
      />
      <Route
        path={tabToRouteBinding[ManageOrganizationTabs.Clusters].route}
        element={
          <>
            <OrganizationManageClustersList
              onManageClick={(p, c) => handleManageClusterClick(p, c)}
              projectsClusters={projectsWithClusters}
            />
            {permissions.edit_organization && (
              <Button
                onClick={() => doOpenCreateServiceOverlay()}
                className={classes.createButton}
                data-cy="clusters-tab-add-cluster-button"
              >
                {t('management.clusters_list.create')}
              </Button>
            )}
          </>
        }
      />
      <Route
        path={tabToRouteBinding[ManageOrganizationTabs.Members].route}
        element={
          <div>
            <InvitedToOrganizationList organizationId={organizationId} />
            <OrganizationMembersList organizationId={organizationId} />
            {permissions.edit_organization && (
              <InviteMember organizationId={organizationId} text={t('management.members_list.add')} />
            )}
          </div>
        }
      />
      <Route
        path={tabToRouteBinding[ManageOrganizationTabs.Teams].route}
        element={organization && <TeamsTab organization={{ id: organizationId, name: organization.name }} />}
      />
      {permissions.edit_organization && (
        <Route
          path={tabToRouteBinding[ManageOrganizationTabs.SSO].route}
          element={organization && <SsoTab organization={{ id: organizationId, name: organization.name }} />}
        />
      )}
    </Routes>
  );

  const renderTabMenu = () => (
    <Tabs
      orientation="vertical"
      aria-label="organization manage tabs"
      indicatorColor="primary"
      scrollButtons={false}
      value={
        matchPath(getPathForRoute(ManageOrganizationTabs.Teams), location.pathname)
          ? getPathForTab(ManageOrganizationTabs.Teams)
          : location.pathname
      }
      className={classes.verticalTabs}
    >
      <Tab
        value={baseUrl}
        label={t('management.details_tab')}
        component={NavLink}
        icon={<InfoIcon />}
        to={tabToRouteBinding[ManageOrganizationTabs.General].tab}
      />
      <Tab
        value={getPathForTab(ManageOrganizationTabs.Projects)}
        label={t('management.projects_tab')}
        component={NavLink}
        icon={<AccountTreeIcon />}
        to={getPathForTab(ManageOrganizationTabs.Projects)}
        data-cy="organization-management-projects-tab"
      />
      <Tab
        value={getPathForTab(ManageOrganizationTabs.Clusters)}
        label={t('management.clusters_tab')}
        component={NavLink}
        icon={<WidgetsIcon />}
        to={getPathForTab(ManageOrganizationTabs.Clusters)}
        data-cy="organization-management-clusters-tab"
      />
      <Tab
        value={getPathForTab(ManageOrganizationTabs.Members)}
        label={t('management.members_tab')}
        component={NavLink}
        icon={<PersonIcon />}
        to={getPathForTab(ManageOrganizationTabs.Members)}
        data-cy="organization-management-members-tab"
      />
      <Tab
        value={getPathForTab(ManageOrganizationTabs.Teams)}
        label={t('management.teams_tab')}
        component={NavLink}
        icon={<GroupsIcon />}
        to={getPathForTab(ManageOrganizationTabs.Teams)}
        data-cy="organization-management-teams-tab"
      />
      {permissions.edit_organization && (
        <Tab
          value={getPathForTab(ManageOrganizationTabs.SSO)}
          label={t('management.sso_tab')}
          component={NavLink}
          icon={<KeyIcon />}
          to={getPathForTab(ManageOrganizationTabs.SSO)}
          data-cy="organization-management-sso-tab"
        />
      )}
    </Tabs>
  );

  return (
    <Grid container classes={{ root: classes.root }}>
      <Grid item xs={12} classes={{ item: classes.wrapperBreadcrumbs }}>
        <Breadcrumbs>
          <RouterLink to="/">{t('navigation:page.home')}</RouterLink>
          <RouterLink
            to={prepareRoutePath(BasicRoutes.ORGANIZATION_PAGE, {
              ':organization_id': organizationId,
            })}
          >
            {!isFetching && organization?.name}
          </RouterLink>
          <Typography>{t('management.manage_organization')}</Typography>
        </Breadcrumbs>
      </Grid>
      <Typography classes={{ h3: classes.h3 }} variant="h3">
        {t('management.manage_organization')}
      </Typography>
      <Grid item xs={12}>
        <div className={classes.mainRow}>
          <div className={classes.tabMenu}>{renderTabMenu()}</div>
          <div className={classes.content}>{renderTabContent()}</div>
        </div>
      </Grid>
    </Grid>
  );
});

export const ManageOrganizationPage = connector(ManageOrganizationPageComponent);
